import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Coins, RefreshCw } from 'lucide-react'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { WireInstructionsDialog } from '@/components/billing/wire-instructions-dialog'
import { toast } from '@/hooks/use-toast'
import { get_upcoming_payment, list_cabinet_subscriptions, renew_cabinet_subscription } from '@/api/cabinet'
import type { Subscription, SubscriptionStatus } from '@/types/subscription'

const toDate = (v?: string | number | null) => {
  if (v == null) return null
  return new Date(typeof v === 'number' ? v * 1000 : v)
}

const STATUS_STYLE: Record<SubscriptionStatus, string> = {
  active: 'bg-green-600 hover:bg-green-600 text-white',
  trialing: 'bg-blue-600 hover:bg-blue-600 text-white',
  free: 'bg-blue-600 hover:bg-blue-600 text-white',
  pending: 'bg-amber-500 hover:bg-amber-500 text-white',
  past_due: 'bg-red-600 hover:bg-red-600 text-white',
  paused: '',
  canceled: '',
}

export default function CabinetSubscriptions() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [renewing, setRenewing] = useState(false)
  const [instructionsOpen, setInstructionsOpen] = useState(false)

  const subsQuery = useQuery({
    queryKey: ['cabinet-subscriptions', 'list'],
    queryFn: () => list_cabinet_subscriptions({ page_size: 20 }),
  })
  const subs: Subscription[] = subsQuery.data?.results ?? []
  const current = subs.find((s) => ['active', 'past_due', 'trialing'].includes(s.status))
  const hasPending = subs.some((s) => s.status === 'pending')

  // upcoming-payment 400s when there is no active/past_due subscription
  const upcomingQuery = useQuery({
    queryKey: ['cabinet-upcoming-payment'],
    queryFn: get_upcoming_payment,
    enabled: current?.status === 'active' || current?.status === 'past_due',
    retry: false,
  })
  const upcoming = upcomingQuery.data

  const money = (cents?: number | null, currency?: string | null) =>
    cents == null ? '-' : `${(cents / 100).toFixed(2)} ${currency ?? 'MAD'}`

  const statusLabel = (s: SubscriptionStatus) => t(`subscription_status_${s}`) || s

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['cabinet-subscriptions'] })
    queryClient.invalidateQueries({ queryKey: ['cabinet-upcoming-payment'] })
    queryClient.invalidateQueries({ queryKey: ['cabinet-invoices'] })
  }

  const renew = async () => {
    setRenewing(true)
    try {
      await renew_cabinet_subscription()
      refresh()
      setInstructionsOpen(true)
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        // renewal already requested: just remind the cabinet where to pay
        setInstructionsOpen(true)
      } else {
        toast({
          title: t('renew_failed') || 'Unable to request renewal',
          description: isAxiosError(e) ? e.response?.data?.detail || e.message : undefined,
          variant: 'destructive',
        })
      }
    } finally {
      setRenewing(false)
    }
  }

  const canRenew = current?.status === 'active' || current?.status === 'past_due'

  return (
    <>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <RefreshCw className="h-5 w-5" />
            </span>
          }
          title={t('subscriptions') || 'Subscriptions'}
          description={t('cabinet_subscriptions_desc') || 'View your cabinet subscriptions.'}
        />

        {subsQuery.isLoading ? (
          <div className='py-8 text-center'>{t('loading') || 'Loading'}...</div>
        ) : subsQuery.isError ? (
          <div className='py-8 text-center text-red-600'>{t('subscriptions_load_error') || 'Failed to load subscriptions'}</div>
        ) : subs.length === 0 ? (
          <div className='py-8 text-center'>{t('no_subscriptions') || 'No subscriptions yet.'}</div>
        ) : (
          <div className='space-y-6'>
            {current && (
              <Card>
                <CardContent className='space-y-4 p-5'>
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <div className='text-lg font-medium'>{current.plan_name ?? `${t('plan') || 'Plan'} #${current.plan}`}</div>
                    <Badge className={STATUS_STYLE[current.status]}>{statusLabel(current.status)}</Badge>
                  </div>
                  <div className='flex items-center gap-2 text-sm'>
                    <Coins className='h-4 w-4 text-amber-600' />
                    {money(current.amount_cents, current.currency)}
                    {current.cadence && <span className='text-muted-foreground'>/ {t(current.cadence) || current.cadence}</span>}
                  </div>
                  {current.cancel_at_period_end && (
                    <div className='text-sm text-amber-600'>
                      {t('cancel_at_period_end_notice') || 'This subscription will not renew after the current period.'}
                    </div>
                  )}

                  {canRenew && (
                    <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/40 p-4'>
                      <div className='text-sm'>
                        <div className='font-medium'>{t('upcoming_payment') || 'Upcoming payment'}</div>
                        {upcoming ? (
                          <div className='text-muted-foreground'>
                            {money(upcoming.amount_cents, upcoming.currency)}
                            {upcoming.due_date != null && ` · ${t('due') || 'Due'} ${toDate(upcoming.due_date)?.toLocaleDateString()}`}
                            {upcoming.days_until_due != null && ` (${upcoming.days_until_due} ${t('days') || 'days'})`}
                          </div>
                        ) : (
                          <div className='text-muted-foreground'>{upcomingQuery.isLoading ? '...' : '-'}</div>
                        )}
                      </div>
                      <Button onClick={renew} disabled={renewing}>
                        {hasPending ? (t('view_payment_instructions') || 'Payment details') : (t('renew_now') || 'Renew now')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div>
              <h3 className='mb-3 text-sm font-medium text-muted-foreground'>{t('subscription_history') || 'History'}</h3>
              <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {subs.map((s) => {
                  const start = toDate(s.current_period_start)
                  const end = toDate(s.current_period_end)
                  return (
                    <Card key={s.id}>
                      <CardContent className='space-y-2 p-5'>
                        <div className='flex items-center justify-between'>
                          <div className='font-medium'>{s.plan_name ?? `${t('plan') || 'Plan'} #${s.plan}`}</div>
                          <Badge className={STATUS_STYLE[s.status]}>{statusLabel(s.status)}</Badge>
                        </div>
                        <div className='flex items-center gap-2 text-sm'>
                          <Coins className='h-4 w-4 text-amber-600' />
                          {money(s.amount_cents, s.currency)}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {start && end
                            ? `${t('period') || 'Period'}: ${start.toLocaleDateString()} → ${end.toLocaleDateString()}`
                            : t('no_period_info') || 'No period info'}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </Main>
      <WireInstructionsDialog open={instructionsOpen} onOpenChange={setInstructionsOpen} />
    </>
  )
}
